<script lang="ts">
  import {onDestroy} from 'svelte';
  import {doc, setDoc} from 'firebase/firestore';

  import {
    type ProjectFormData,
    type ProjectDraftFormData,
    type Media,
    type ImageMedia,
    type ImageFile,
    ProjectType
  } from '../../types/project';

  import {
    consultingProjectsDb,
    developmentProjectsDb
  } from '../../utils/firebase';
  import uploadFiles from '../../utils/upload-files';
  import deleteFiles from '../../utils/delete-files';
  import isProjectDraft from '../../utils/project-draft';

  import {projects} from '../../stores/projects';
  import {user} from '../../stores/user';

  import ProjectForm from '../project-form/ProjectForm.svelte';

  export let onSuccess: () => void;
  export let onError: (error: unknown) => void;
  export let id = '';

  // Update project (once when subscribed and on each user update)
  const unsubscribeUserStore = user.subscribe(async () => {
    if (id) {
      await projects.fetchProject(id);
    }
  });

  onDestroy(unsubscribeUserStore);

  $: project = $projects.get(id);

  $: db =
    project?.type === ProjectType.CONSULTING
      ? consultingProjectsDb
      : developmentProjectsDb;

  async function onSubmit(
    projectData: ProjectFormData | ProjectDraftFormData,
    filesToUpload: Array<ImageFile>,
    captions: Record<string, string>
  ) {
    if (!project) {
      return;
    }

    try {
      // Delete files
      const filenames = (projectData.media || ([] as Array<Media>)).map(media =>
        'name' in media ? media.name : null
      );
      const filesToDelete = (project.media || ([] as Array<Media>))
        .filter(({type}) => type === 'image')
        .filter<ImageMedia>((media): media is ImageMedia => {
          const name = 'name' in media ? media.name : null;
          return !filenames.includes(name);
        });

      if (filesToDelete.length) {
        try {
          await deleteFiles(filesToDelete);
        } catch (error) {
          console.error('Error', error);
          onError(error);
          return;
        }
      }

      let uploadedMedia: ImageMedia[] = [];

      // Upload new files
      if (filesToUpload && filesToUpload.length) {
        try {
          uploadedMedia = await uploadFiles(
            project.type,
            id,
            [...filesToUpload],
            captions
          );
        } catch (error) {
          console.error('Error', error);
          onError(error);
          return;
        }
      }

      // Update project data
      try {
        const docRef = doc(db, id);
        const isDraft = isProjectDraft(projectData, project?.type);

        const updatedProject = {
          ...projectData,
          media: [...(projectData.media || []), ...uploadedMedia],
          isDraft,
          createdAt: project.createdAt
        };
        await setDoc(docRef, updatedProject);
      } catch (error) {
        console.error('Error', error);
        onError(error);
        return;
      }

      onSuccess();
    } catch (error) {
      console.error('Error', error);
      onError(error);
    }
  }
</script>

<style lang="postcss">
  .formContainer {
    align-items: center;
    margin: 0 auto;
  }
</style>

{#if project}
  <div class="formContainer">
    <ProjectForm
      initialValues={project}
      projectType={project.type}
      {onSubmit} />
  </div>
{/if}
